import {
  DOWNLAOD_MULTIPLE_ASSET,
  DOWNLOAD_READY_ASSETS_API,
  UPDATE_BANDWIDTH,
} from '../components/Dam/DAM_API/urls';
import Axios from './Axios';

/* ----------------------------------
   CONFIG
---------------------------------- */

// Set this once the backend exposes a cancel route, e.g.
//   `${BASE_URL}/dam/download/cancel`   ->  POST { requestId }
// While it's null, cancel only stops the browser reading; the server keeps
// writing and will still record the download as complete.
const CANCEL_DOWNLOAD_API = null;

// Bytes pulled before a user cancels were still real bandwidth.
// IMPORTANT: if your backend also meters bandwidth server-side, leave this
// false or you'll double-count cancelled transfers.
const REPORT_BANDWIDTH_ON_CANCEL = true;

/* ----------------------------------
   PROGRESS CONFIG

   Lives here (not in the context) to break the circular import
   context -> download -> context. The context re-exports it, so every
   existing `import { DOWNLOAD_PROGRESS } from 'context/DownloadAssetsContext'`
   keeps working.
---------------------------------- */

export const DOWNLOAD_PROGRESS = {
  PREPARING: { start: 0, end: 10 },
  PROCESSING: { start: 10, end: 20 },
  DOWNLOADING: { start: 20, end: 99 },
  DONE: { start: 99, end: 100 },
};

export const UPLOAD_PROGRESS = {
  PREPARING: { start: 0, end: 10 },
  UPLOADING: { start: 10, end: 85 },
  PROCESSING: { start: 85, end: 95 },
  DONE: { start: 95, end: 100 },
};

export const mapPhaseProgress = (phase, percent = 0, states = DOWNLOAD_PROGRESS) => {
  const cfg = states[phase];
  if (!cfg) return 0;

  if (cfg.start === cfg.end) return cfg.end;

  return Math.min(cfg.end, cfg.start + Math.floor(((cfg.end - cfg.start) * percent) / 100));
};

/* ----------------------------------
   FILENAME HELPERS
---------------------------------- */

// handles: filename="a.zip" | filename=a.zip | filename*=UTF-8''a%20b.png
export const parseContentDisposition = (cd) => {
  if (!cd) return null;

  const utf8 = cd.match(/filename\*=\s*UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }

  const quoted = cd.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted) return quoted[1];

  const bare = cd.match(/filename\s*=\s*([^;]+)/i);
  if (bare) return bare[1].trim();

  return null;
};

export const extensionOf = (fileName) => {
  if (!fileName || !fileName.includes('.')) return '';
  return fileName.split('.').pop().toLowerCase();
};

/* ----------------------------------
   START DOWNLOAD (create the async job)

   The second argument is accepted for call-signature compatibility but
   deliberately IGNORED. The POST always goes to DOWNLAOD_MULTIPLE_ASSET.
---------------------------------- */

export const startDownloadAssets = async (_data, apiURL) => {
  const response = await Axios.post( apiURL || DOWNLAOD_MULTIPLE_ASSET, _data, {
    validateStatus: () => true,
  });


  console.log('startDownloadAssets response', response.status, response.data);
  // async job created -> a socket event will drive the rest
  if (response.status === 202) {
    return {
      status: response.data?.status,
      downloadId: response.data?.requestId,
      fileName: response.data?.fileName,
      immediate: false,
    };
  }

  // backend says ready right away -> no socket event will follow
  if (response.status === 200) {
    return {
      status: 'READY',
      downloadId: response.data?.id,
      fileName: response.data?.fileName,
      immediate: true,
    };
  }

  throw new Error(`Failed to start download (${response.data?.message})`);
};

/* ----------------------------------
   DOWNLOAD TRACKERS (per tab)
---------------------------------- */

const activeDownloads = new Map(); // downloadId -> { loadedBytes }  (in flight)
const attemptedDownloads = new Set(); // downloadId -> ever fetched, success or not
const downloadControllers = new Map(); // downloadId -> AbortController
const cancelledDownloads = new Set(); // downloadId -> user asked to stop

export const hasActiveDownloads = () => activeDownloads.size > 0;

export const isDownloadInFlight = (downloadId) => activeDownloads.has(downloadId);

export const isDownloadCancelled = (downloadId) => cancelledDownloads.has(downloadId);

// escape hatch: lets the UI offer a manual retry for a genuinely failed id
export const resetDownloadAttempt = (downloadId) => {
  attemptedDownloads.delete(downloadId);
  cancelledDownloads.delete(downloadId);
};

/* ----------------------------------
   SERVER-SIDE CANCEL

   Aborting the XHR only closes the browser's end of the connection. The
   server's handler keeps streaming until it hits a broken pipe — and if the
   response was already buffered it never notices at all, which is why the job
   still shows as a completed download. Only an explicit call can stop it.
---------------------------------- */

export const cancelDownloadOnServer = async (downloadId) => {
  if (!CANCEL_DOWNLOAD_API || !downloadId) return false;

  try {
    await Axios.post(CANCEL_DOWNLOAD_API, { requestId: downloadId });
    return true;
  } catch (err) {
    console.error('[download] server-side cancel failed for', downloadId, err);
    return false;
  }
};

/* ----------------------------------
   CANCEL

   Works in both phases:
   - job still PREPARING/PROCESSING (no HTTP request yet) -> flagged, so the
     later READY never starts a transfer
   - transfer already running -> AbortController kills the XHR mid-stream
---------------------------------- */

export const cancelDownload = (downloadId) => {
  if (!downloadId) return false;

  cancelledDownloads.add(downloadId);

  const controller = downloadControllers.get(downloadId);
  const wasInFlight = Boolean(controller);

  if (controller) {
    controller.abort();
    downloadControllers.delete(downloadId);
  }

  // fire and forget — the UI shouldn't wait on this
  cancelDownloadOnServer(downloadId);

  return wasInFlight;
};

export const cancelAllDownloads = () => {
  const ids = new Set([...activeDownloads.keys(), ...downloadControllers.keys()]);
  ids.forEach((id) => cancelDownload(id));
  return [...ids];
};

const isAbortError = (err) =>
  err?.code === 'ERR_CANCELED' ||
  err?.name === 'CanceledError' ||
  err?.name === 'AbortError' ||
  err?.message === 'canceled';

/* ----------------------------------
   BANDWIDTH REPORTING
---------------------------------- */

// survives tab close
const reportBandwidthBeacon = (fileSizeMb) => {
  const payload = JSON.stringify({ downloadBandWidth: fileSizeMb });

  try {
    if (navigator.sendBeacon) {
      // text/plain avoids a CORS preflight — beacons cannot preflight
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
      navigator.sendBeacon(UPDATE_BANDWIDTH, blob);
    } else {
      fetch(UPDATE_BANDWIDTH, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });
    }
  } catch {
    /* nothing we can do while the page is unloading */
  }
};

const reportBandwidth = async (bytes) => {
  if (!bytes) return;

  const fileSizeMb = Number((bytes / (1024 * 1024)).toFixed(2));

  try {
    await Axios.post(UPDATE_BANDWIDTH, { downloadBandWidth: fileSizeMb });
  } catch (err) {
    // don't let a failed bandwidth report break the download UX
    console.error('update-bandwidth failed:', err);
  }
};

// called from the provider's pagehide handler
export const flushPendingBandwidth = () => {
  activeDownloads.forEach(({ loadedBytes }, downloadId) => {
    if (loadedBytes) {
      const fileSizeMb = Number((loadedBytes / (1024 * 1024)).toFixed(2));
      reportBandwidthBeacon(fileSizeMb);
    }
    activeDownloads.delete(downloadId);
  });
};

/* ----------------------------------
   DOWNLOAD FILE

   onMeta({ fileName, fileType, totalBytes }) fires as soon as response headers
   arrive — mid-transfer, well before the blob is ready — so the progress row
   can show the real name instead of a placeholder.
---------------------------------- */

export const downloadAssetsFile = async (_data, onProgress, onMeta) => {
  let totalSize = 0;
  let resolvedName = null;
  const downloadId = _data.downloadId;

  if (!downloadId) {
    throw new Error('downloadAssetsFile called without a downloadId');
  }

  // cancelled while the job was still preparing server-side
  if (cancelledDownloads.has(downloadId)) {
    const err = new Error('Download cancelled');
    err.cancelled = true;
    throw err;
  }

  // A ready-link is single-use on the server: a second GET returns 410 Gone.
  // Never fetch the same id twice in this tab, even after the first finished.
  if (attemptedDownloads.has(downloadId)) {
    console.warn('[download] skipping duplicate fetch for', downloadId);
    return;
  }

  const controller = new AbortController();

  attemptedDownloads.add(downloadId);
  downloadControllers.set(downloadId, controller);
  activeDownloads.set(downloadId, { loadedBytes: 0 });

  const publishMeta = (fileName, bytes) => {
    if (!fileName || resolvedName === fileName) return;

    resolvedName = fileName;

    if (onMeta) {
      onMeta({
        fileName,
        fileType: extensionOf(fileName),
        totalBytes: bytes || 0,
      });
    }
  };

  try {
    const response = await Axios.get(`${DOWNLOAD_READY_ASSETS_API}/${downloadId}`, {
      responseType: 'blob',
      validateStatus: () => true,
      signal: controller.signal,

      onDownloadProgress: (e) => {
        const entry = activeDownloads.get(downloadId);
        if (entry) entry.loadedBytes = e.loaded;

        const xhr = e.target || e.event?.target; // xhr adapter: target is the XMLHttpRequest

        if (!totalSize) {
          const customSize = xhr?.getResponseHeader?.('x-file-size');

          if (customSize) {
            totalSize = Number(customSize);
          } else if (e.total) {
            totalSize = e.total;
          }
        }

        // headers are already available here, so the real name can be shown
        // while the bytes are still streaming
        if (!resolvedName) {
          const cd = xhr?.getResponseHeader?.('content-disposition');
          publishMeta(parseContentDisposition(cd), totalSize);
        }

        if (!totalSize) return;

        const rawPercent = Math.round((e.loaded * 100) / totalSize);
        const mappedPercent = mapPhaseProgress('DOWNLOADING', rawPercent);

        if (onProgress) onProgress(mappedPercent);
      },
    });

    if (response.status !== 200) {
      const err = new Error(`File download failed (${response.status})`);
      err.status = response.status;
      throw err;
    }

    // cancel can land between the last progress tick and the response settling
    if (cancelledDownloads.has(downloadId)) {
      const err = new Error('Download cancelled');
      err.cancelled = true;
      throw err;
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });

    // second chance at the name if the progress ticks never exposed it
    const filename =
      resolvedName || parseContentDisposition(response.headers['content-disposition']) || 'images.zip';

    publishMeta(filename, blob.size);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);

    const bytes = blob.size || totalSize || 0;

    // completed normally -> stop tracking so we don't double-report on unload
    activeDownloads.delete(downloadId);
    downloadControllers.delete(downloadId);

    await reportBandwidth(bytes);

    return { fileName: filename, bytes };
  } catch (err) {
    const entry = activeDownloads.get(downloadId);
    const partialBytes = entry?.loadedBytes || 0;

    activeDownloads.delete(downloadId);
    downloadControllers.delete(downloadId);

    if (isAbortError(err) || err.cancelled) {
      if (REPORT_BANDWIDTH_ON_CANCEL && partialBytes) {
        // fire and forget — the caller is unwinding
        reportBandwidth(partialBytes);
      }

      const cancelErr = new Error('Download cancelled');
      cancelErr.cancelled = true;
      throw cancelErr;
    }

    throw err;
  }
};